import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useToast } from '../components/useToast'
import { useLanguage } from '../hooks/useLanguage'

export default function Alertas() {
  const { t } = useLanguage()
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const toast = useToast()

  const [form, setForm] = useState({
    title: '',
    description: '',
    original_amount: '',
    late_fee_percent: '',
    due_date: ''
  })

  const inputStyle = {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    fontSize: 14,
    outline: 'none',
    background: 'var(--color-surface)',
    color: 'var(--color-text)'
  }

  function handleChange(field) {
    return (e) => {
      setForm(prev => ({
        ...prev,
        [field]: e.target.value
      }))
    }
  }

  function handleAmountChange(value) {
    const limpio = value.replace(/\D/g, '')
    const formateado = new Intl.NumberFormat('es-CO').format(limpio)
    setForm(prev => ({
      ...prev,
      original_amount: formateado
    }))
  }

  async function getUser() {
    const { data } = await supabase.auth.getUser()
    return data.user
  }

  async function fetchAlerts() {
    setLoading(true)

    const user = await getUser()

    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('user_id', user.id)
      .order('due_date', { ascending: true })

    if (!error) {
      setAlerts(data)
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchAlerts()
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()

    setError('')

    const amount = parseFloat(
      form.original_amount.replace(/\./g, ''))

    if (
      !form.title ||
      !form.original_amount ||
      !form.late_fee_percent ||
      !form.due_date
    ) {
      setError(t('alertas_campos_obligatorios'))
      return
    }
    if (form.title.trim().length > 50) { setError(t('alertas_nombre_max_50')); return }
    if (form.description.trim().length > 200) { setError(t('alertas_desc_max_200')); return }

    const user = await getUser()

    const { error } = await supabase
      .from('alerts')
      .insert([{
        user_id: user.id,
        title: form.title,
        description: form.description,
        original_amount: amount,
        late_fee_percent: parseFloat(form.late_fee_percent),
        due_date: form.due_date
      }])

    if (error) {
      toast.error(t('toast_error_guardar_alerta'))
      return
    }

    setForm({
      title: '',
      description: '',
      original_amount: '',
      late_fee_percent: '',
      due_date: ''
    })

    fetchAlerts()
  }

  async function handleDelete(id) {
    const confirmDelete = window.confirm(
      t('alertas_confirmar_eliminar')
    )

    if (!confirmDelete) return

    const { error } = await supabase
      .from('alerts')
      .delete()
      .eq('id', id)

    if (!error) {
      fetchAlerts()
    }
  }

  function calculateCurrentAmount(alert) {
    const today = new Date()
    const dueDate = new Date(alert.due_date)

    const diffTime = today - dueDate

    const daysLate = Math.max(
      0,
      Math.floor(diffTime / (1000 * 60 * 60 * 24))
    )

    const penalty =
      alert.original_amount *
      (alert.late_fee_percent / 100) *
      daysLate

    return {
      daysLate,
      total: alert.original_amount + penalty
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg)'
    }}>

      {/* HEADER */}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 20,
          fontWeight: 600
        }}>
          {t('alertas_header')}
        </h2>

        <button
          onClick={() => window.history.back()}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid var(--color-border)',
            background: 'transparent',
            fontSize: 12,
            color: 'var(--color-text)'
          }}
        >
          {t('volver')}
        </button>
      </header>

      <main style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: 20
      }}>

        {/* CARD SUPERIOR */}
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-md)',
          padding: 16,
          marginBottom: 20,
          border: '1px solid var(--color-border)'
        }}>
          <div style={{
            fontSize: 12,
            color: '#c2410c'
          }}>
            {t('alertas_activas')}
          </div>

          <div style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#ea580c',
            fontFamily: 'var(--font-display)'
          }}>
            {alerts.length}
          </div>
        </div>

        {/* FORMULARIO */}
        <form
          onSubmit={handleSubmit}
          style={{
            background: 'var(--color-surface)',
            padding: 16,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}
        >

          <input
            type="text"
            placeholder={t('alertas_nombre_placeholder')}
            value={form.title}
            onChange={handleChange('title')}
            style={inputStyle}
          />

          <textarea
            placeholder={t('descripcion_placeholder')}
            value={form.description}
            onChange={handleChange('description')}
            rows={3}
            style={{
              ...inputStyle,
              resize: 'none'
            }}
          />

          <input
            type="text"
            placeholder={t('alertas_valor_placeholder')}
            value={form.original_amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            style={inputStyle}
          />

          <input
            type="number"
            placeholder={t('alertas_mora_placeholder')}
            value={form.late_fee_percent}
            onChange={handleChange('late_fee_percent')}
            style={inputStyle}
          />

          <input
            type="date"
            value={form.due_date}
            onChange={handleChange('due_date')}
            style={inputStyle}
          />

          {error && (
            <div style={{
              color: '#ef4444',
              fontSize: 13
            }}>
              {error}
            </div>
          )}

          <button
            style={{
              marginTop: 6,
              padding: '12px',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {t('alertas_crear_btn')}
          </button>
        </form>

        {/* LISTA */}
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)'
        }}>
          {alerts.length === 0 ? (
            <p style={{
              padding: 16,
              fontSize: 13,
              color: 'var(--color-muted)'
            }}>
              {t('alertas_sin_registradas')}
            </p>
          ) : (
            alerts.map(alert => {
              const calc = calculateCurrentAmount(alert)

              const expired = calc.daysLate > 0

              return (
                <div
                  key={alert.id}
                  style={{
                    padding: 16,
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}
                >

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{
                        fontWeight: 700
                      }}>
                        {alert.title}
                      </div>

                      <div style={{
                        fontSize: 12,
                        color: 'var(--color-muted)'
                      }}>
                        {alert.description}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDelete(alert.id)}
                      style={{
                        background: '#3f1d1d',
                        border: '1px solid #7f1d1d',
                        color: '#f87171',
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️
                    </button>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 10
                  }}>

                    <div>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--color-muted)'
                      }}>
                        {t('alertas_valor_original')}
                      </div>

                      <div style={{
                        fontWeight: 700
                      }}>
                        ${Number(alert.original_amount)
                          .toLocaleString('es-CO')}
                      </div>
                    </div>

                    <div>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--color-muted)'
                      }}>
                        {t('alertas_mora_diaria')}
                      </div>

                      <div style={{
                        fontWeight: 700,
                        color: '#ea580c'
                      }}>
                        {alert.late_fee_percent}%
                      </div>
                    </div>

                    <div>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--color-muted)'
                      }}>
                        {t('alertas_fecha_limite')}
                      </div>

                      <div style={{
                        fontWeight: 700
                      }}>
                        {new Date(alert.due_date)
                          .toLocaleDateString()}
                      </div>
                    </div>

                  </div>

                  {expired && (
                    <div style={{
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      padding: 12,
                      borderRadius: 10
                    }}>
                      <div style={{
                        color: '#ef4444',
                        fontWeight: 700,
                        marginBottom: 4
                      }}>
                        {t('alertas_retraso_pre')} {calc.daysLate} {t('alertas_retraso_post')}
                      </div>

                      <div style={{
                        fontSize: 14,
                        color: 'var(--color-text)'
                      }}>
                        {t('alertas_nuevo_valor')}
                        <span style={{
                          fontWeight: 700,
                          marginLeft: 6
                        }}>
                          ${calc.total.toLocaleString('es-CO')}
                        </span>
                      </div>
                    </div>
                  )}

                </div>
              )
            })
          )}
        </div>

      </main>
    </div>
  )
}